import { useMemo, useState } from 'react';
import type { LiveEmployee } from '@geotracker/contracts';
import { Badge, Dot, FlagBadge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  formatAge,
  formatBattery,
  formatCoordinates,
  formatDistance,
  formatDuration,
  formatSpeed,
} from '@/lib/format';

const STALE_SECONDS = 300;

export type RosterFilter = 'all' | 'moving' | 'stationary' | 'attention';

const FILTERS: { id: RosterFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'moving', label: 'Moving' },
  { id: 'stationary', label: 'Stationary' },
  { id: 'attention', label: 'Needs attention' },
];

/**
 * An employee needs attention when something is wrong with the TRACKING, not
 * when they happen to be sitting still.
 *
 * `long_stationary` is deliberately excluded: someone at a customer site for
 * forty minutes is doing their job, and a filter that flags them alongside a
 * phone with revoked permissions trains an admin to ignore the filter.
 */
const ATTENTION_FLAGS = new Set([
  'not_capturing',
  'gps_disabled',
  'mock_gps',
  'offline',
  'stale',
  'session_stale',
  'battery_low',
  'sync_backlog',
  'never_reported',
]);

export function needsAttention(employee: LiveEmployee): boolean {
  return employee.flags.some((f) => ATTENTION_FLAGS.has(f));
}

interface Props {
  employees: LiveEmployee[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

export function Roster({ employees, selectedId, onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<RosterFilter>('all');

  const counts = useMemo(
    () => ({
      all: employees.length,
      moving: employees.filter((e) => e.movement.state === 'moving').length,
      stationary: employees.filter((e) => e.movement.state === 'stationary').length,
      attention: employees.filter(needsAttention).length,
    }),
    [employees],
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return employees.filter((e) => {
      if (needle) {
        const haystack =
          `${e.employee.name} ${e.employee.code} ${e.employee.designation ?? ''} ${e.dwell?.place ?? ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (filter === 'moving') return e.movement.state === 'moving';
      if (filter === 'stationary') return e.movement.state === 'stationary';
      if (filter === 'attention') return needsAttention(e);
      return true;
    });
  }, [employees, query, filter]);

  return (
    <div className="flex min-h-0 flex-col">
      <div className="space-y-2 border-b border-border p-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, code or place…"
          aria-label="Search employees"
          className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
        />
        {/* Filters in one row above the list, with their counts on them: a
            filter that does not say how many it will show is a guess. */}
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-2xs font-medium transition-colors',
                filter === f.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-muted',
              )}
            >
              {f.label}
              <span className="ml-1 tabular-nums opacity-70">{counts[f.id]}</span>
            </button>
          ))}
        </div>
      </div>

      <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
        {visible.length === 0 ? (
          <li className="p-6 text-center text-sm text-muted-foreground">
            {employees.length === 0
              ? 'No employees are being tracked yet.'
              : 'Nobody matches that filter.'}
          </li>
        ) : null}

        {visible.map((e) => (
          <li key={e.employee.id}>
            <RosterRow
              employee={e}
              selected={selectedId === e.employee.id}
              onSelect={() => onSelect(e.employee.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function RosterRow({
  employee,
  selected,
  onSelect,
}: {
  employee: LiveEmployee;
  selected: boolean;
  onSelect: () => void;
}) {
  const p = employee.position;
  const stale = (p?.ageSeconds ?? Infinity) > STALE_SECONDS;
  const moving = employee.movement.state === 'moving';

  const tone = employee.state.isMock
    ? 'danger'
    : !p
      ? 'muted'
      : stale
        ? 'warning'
        : moving
          ? 'moving'
          : 'stopped';

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected}
      className={cn(
        'w-full px-3 py-2.5 text-left transition-colors',
        selected ? 'bg-primary/5 ring-1 ring-inset ring-primary/30' : 'hover:bg-muted/60',
      )}
    >
      <div className="flex items-center gap-2">
        <Dot tone={tone} pulse={moving && !stale} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{employee.employee.name}</span>
        <span className="shrink-0 text-2xs tabular-nums text-muted-foreground">
          {formatDistance(employee.today.distanceMeters)}
        </span>
      </div>

      {/*
       * The one line that answers the actual question. Moving people get speed
       * and course; stationary people get how long and where — which is the
       * whole reason the dwell anchor exists, and is far more useful than
       * repeating a timestamp everyone has to subtract from the clock.
       *
       * Muted and prefixed once the fix goes stale. The server keeps the last
       * known movement rather than blanking it — the same choice it makes for
       * the position itself — but "34 km/h NE" printed in the present tense
       * beside a fix from ninety minutes ago reads as a claim about now, and
       * the map already refuses to draw a direction arrow for the same reason.
       */}
      <div
        className={cn(
          'mt-1 flex items-baseline gap-1.5 pl-4 text-xs',
          stale && 'text-muted-foreground',
        )}
      >
        {stale && p ? <span className="shrink-0">last known</span> : null}
        {moving ? (
          <>
            <span
              className={cn('font-medium tabular-nums', !stale && 'text-track-moving')}
            >
              {formatSpeed(employee.movement.speedMps)}
            </span>
            {employee.movement.compass ? (
              <span className="text-muted-foreground">{employee.movement.compass}</span>
            ) : null}
          </>
        ) : employee.dwell ? (
          <>
            <span
              className={cn('font-medium tabular-nums', !stale && 'text-track-stopped')}
            >
              {formatDuration(employee.dwell.seconds)}
            </span>
            <span className="min-w-0 truncate text-muted-foreground">
              at{' '}
              {employee.dwell.place ??
                formatCoordinates(employee.dwell.latitude, employee.dwell.longitude)}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">
            {p ? 'Stationary' : 'Never reported'}
          </span>
        )}
      </div>

      <div className="mt-0.5 flex items-center gap-2 pl-4 text-2xs text-muted-foreground">
        <span className="tabular-nums">{p ? formatAge(p.ageSeconds) : '—'}</span>
        <span>·</span>
        <span className="tabular-nums">
          {formatBattery(employee.state.batteryLevel, employee.state.isCharging)}
        </span>
        {employee.state.outboxPending > 0 ? (
          <>
            <span>·</span>
            {/*
             * How much is waiting AND how old it is. Fifty points queued is a
             * blip when they are a minute old and a four-hour hole in the
             * record when they are not, and the count alone cannot tell the
             * two apart.
             */}
            <span className="tabular-nums">
              {employee.state.outboxPending} queued
              {employee.state.syncLagSeconds !== null && employee.state.syncLagSeconds > 120
                ? `, ${formatDuration(employee.state.syncLagSeconds)} behind`
                : ''}
            </span>
          </>
        ) : null}
      </div>

      {employee.flags.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1 pl-4">
          {employee.flags.slice(0, 3).map((flag) => (
            <FlagBadge key={flag} code={flag} />
          ))}
          {employee.flags.length > 3 ? (
            <Badge tone="muted">+{employee.flags.length - 3}</Badge>
          ) : null}
        </div>
      ) : null}
    </button>
  );
}
